import { Box, Grid, makeStyles, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core"
import React, { useMemo, useRef, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import useAsyncEffect from "use-async-effect"
import useDragScroll from "use-drag-scroll"
import {
  FlexBox,
  LoadingSpinner,
  NoItemsMessage,
  RowBox,
  SearchInput,
  TooltipIcon,
  TrainerChip,
  TrainingFiltersPanel,
  UserAvatar,
  UserTrainingAssessmentCreator,
  UserTrainingAssessmentStatusChipButton,
} from "../../components"
import { UserAssessmentTooltip } from "../../components/Training/UserAssessmentTooltip"
import TrainingAssessmentDownload from "../../components/Training/TrainingAssessmentDownload"
import {
  useLazyQueryAdminableUser,
  useLazyQueryAdminableUsers,
  useLazyQueryTrainingModules,
  USER_STATUS,
} from "../../data"
import { mapToIds, toId, useMountEffect, useWindowSize } from "../../utils"
import { compileAdminableUsersFilters as makeUserFilters } from "../people"

const useStyles = makeStyles((theme) => ({
  filterPanel: {
    backgroundColor: theme.palette.shaded.filter,
  },
  searchInput: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  fullName: {
    fontSize: 16,
    color: theme.palette.text.tinted,
    lineHeight: "20px",
    whiteSpace: "nowrap",
  },
  roles: {
    color: theme.palette.text.light,
    whiteSpace: "nowrap",
  },
  tableHead: {
    position: "sticky",
    backgroundColor: "white",
    zIndex: 1000,
    top: 0,
  },
  tableCellCenter: {
    textAlign: "center",
  },
  tableCellSticky: {
    position: "sticky",
    left: 0,
    backgroundColor: "white",
    zIndex: 999,
  },
  infiniteScrollWrapper: {
    "&&>div": {
      display: "contents",
    },
  },
  infiniteScroll: {
    display: "contents",
  },
  checkboxIcon: {
    color: theme.palette.success.main,
  },
  supportInfo: {
    fontSize: 20,
    cursor: "pointer",
  },
  supportInfoRef: {
    display: "flex",
    alignSelf: "center",
  },
}))

const initialState = {
  filters: [],
  moduleFilters: [],
}

const statusFilter = [USER_STATUS.INVITED, USER_STATUS.ACTIVE]

const isTrainer = (module, user) => {
  const userIds = mapToIds(module.users)
  if (userIds.includes(toId(user))) {
    return true
  }

  return Boolean(module.groups.length) && module.groups.some((group) => mapToIds(user.groups).includes(toId(group)))
}

const AssessmentUserRow = ({ user, modules, classes, onEdit }) => {
  const operandio = user.email?.endsWith("@operandio.com")

  const handleEdit = (edit) => {
    onEdit && onEdit(edit)
  }

  return (
    <TableRow key={toId(user)}>
      <TableCell className={classes.tableCellSticky}>
        <Box display="flex" flexDirection="row">
          <UserAvatar {...user} />
          <Box ml={1}>
            <Box className={classes.fullName}>{user.fullName}</Box>
            <Box className={classes.roles}>
              {!operandio && user.roles.map((role) => role.friendlyName).join(", ")}
              {!operandio && user.roles.length === 0 && "User"}
              {operandio && (
                <RowBox>
                  <Box mr={0.5}>Operandio Support</Box>
                  <TooltipIcon
                    name="information"
                    tooltip="This user does not count towards your billing"
                    className={classes.supportInfo}
                    refClassName={classes.supportInfoRef}
                  />
                </RowBox>
              )}
            </Box>
          </Box>
        </Box>
      </TableCell>
      {modules.map((module) => {
        const trainer = isTrainer(module, user)
        const assessment = !trainer && user.training.assessments.find((item) => toId(module) === toId(item.module))

        return (
          <TableCell key={toId(module)} style={{ cursor: "grab", width: 150, textAlign: "center" }}>
            {trainer && (
              <UserAssessmentTooltip user={user} module={module} userTrainingAssessment={assessment}>
                <TrainerChip />
              </UserAssessmentTooltip>
            )}

            {!trainer && (
              <UserAssessmentTooltip user={user} module={module} userTrainingAssessment={assessment}>
                <UserTrainingAssessmentStatusChipButton
                  assessment={assessment}
                  onClick={() => handleEdit({ user, module, assessment })}
                />
              </UserAssessmentTooltip>
            )}
          </TableCell>
        )
      })}
    </TableRow>
  )
}

const Assessment = () => {
  const classes = useStyles()
  const { height: windowHeight } = useWindowSize()
  const tableHeight = (windowHeight || 1280) - 358
  const [loadAdminableUsers, { data: usersData, loading: adminableUsersLoading, loadMore }] =
    useLazyQueryAdminableUsers({
      variables: {
        status: statusFilter,
      },
    })
  const [loadAdminableUser] = useLazyQueryAdminableUser()
  const [loadTrainingModules, { data: modulesData, loading: modulesLoading }] = useLazyQueryTrainingModules()
  const tableRef = useRef()
  const [edit, setEdit] = useState(null)
  const [searchText, setSearchText] = useState("")
  const [searchTextLoading, setSearchTextLoading] = useState(false)
  const [filters, setFilters] = useState(initialState.filters)
  const [moduleFilters, setModuleFilters] = useState(initialState.moduleFilters)

  useDragScroll({
    sliderRef: tableRef,
    momentumVelocity: 0,
  })

  useMountEffect(() => {
    loadAdminableUsers()
    loadTrainingModules()
  })

  useAsyncEffect(async () => {
    await loadAdminableUsers({
      variables: {
        offset: 0,
        ...makeUserFilters(searchText, filters),
        status: statusFilter,
      },
    })
    if (searchTextLoading) {
      setSearchTextLoading(false)
    }
  }, [filters, loadAdminableUsers, searchText])

  useAsyncEffect(async () => {
    await loadTrainingModules({
      variables: {
        ...moduleFilters.reduce((acc, filter) => {
          acc[filter.type] = [...(acc[filter.type] || []), filter.value]
          return acc
        }, {}),
      },
    })
  }, [moduleFilters, loadTrainingModules])

  const handleChangeSearchText = (text) => {
    setSearchTextLoading(true)
    setSearchText(text)
  }

  const handleFilterChange = (type, event) => {
    const values = event.target.value
    if (type === "trainingCourses") {
      setModuleFilters((prev) => {
        const next = prev.filter((filter) => filter.type !== type)
        next.push(...values.map((value) => ({ type, value })))
        return next
      })
      return
    }

    setFilters((prev) => {
      const next = prev.filter((filter) => filter.type !== type)
      next.push(...values.map((value) => ({ type, value })))
      return next
    })
  }

  const handleClearFilters = () => {
    filters.length && setFilters([])
    moduleFilters.length && setModuleFilters([])
  }

  const handleFilterDelete = ({ type, value }) => {
    if (type === "trainingCourses") {
      setModuleFilters((prev) => prev.filter((filter) => !(filter.type === type && filter.value === value)))
      return
    }
    setFilters((prev) => prev.filter((filter) => !(filter.type === type && filter.value === value)))
  }

  const handleEdit = (value) => {
    setEdit(value)
  }

  const handleCloseEdit = async (updated) => {
    if (updated) {
      await loadAdminableUser({ variables: { id: toId(edit.user) } })
    }
    setEdit(null)
  }

  const makeDownloadFilters = () => ({
    adminableUsers: { ...makeUserFilters(searchText, filters), status: statusFilter },
    trainingModules: {
      trainingCourses: moduleFilters.map((filter) => filter.value),
    },
  })

  const loading = adminableUsersLoading || modulesLoading
  const modules = modulesData?.trainingModules || []

  const items = useMemo(
    () =>
      usersData?.adminableUsers.map((user) => (
        <AssessmentUserRow
          key={toId(user)}
          user={user}
          modules={modulesData?.trainingModules || []}
          classes={classes}
          onEdit={handleEdit}
        />
      )) || [],
    [classes, modulesData, usersData]
  )

  const editUser = edit && usersData ? usersData.adminableUsers.find((user) => toId(user) === toId(edit.user)) : null

  return (
    <>
      {edit && <UserTrainingAssessmentCreator {...edit} user={editUser} onClose={handleCloseEdit} />}

      <Box mb={3}>
        <Grid container alignItems="center" className={classes.filterPanel}>
          <Grid item xs={8}>
            <TrainingFiltersPanel
              filters={[...filters, ...moduleFilters]}
              filtersOptions={{ hasCourses: true, hasGroups: true, hasLocations: true }}
              onChange={handleFilterChange}
              onClear={handleClearFilters}
              onDelete={handleFilterDelete}
            />
          </Grid>
          <Grid item xs={4}>
            <RowBox alignItems="center">
              <FlexBox flexGrow={1}>
                <SearchInput
                  placeholder="Filter people"
                  debounce={300}
                  onChange={handleChangeSearchText}
                  loading={searchTextLoading}
                  inputProps={{ className: classes.searchInput }}
                  boxProps={{ pr: 0, mb: 0, flexGrow: 1 }}
                />
              </FlexBox>
              {items && modules && (
                <Box mx={2}>
                  <TrainingAssessmentDownload filters={makeDownloadFilters()} />
                </Box>
              )}
            </RowBox>
          </Grid>
        </Grid>
      </Box>

      <div
        id="Assessment-scroller"
        ref={tableRef}
        style={{ overflowX: "auto", height: `${tableHeight}px` }}
        data-cy="Assessment-scroller"
      >
        {loading && !usersData && !modulesData && (
          <Box display="flex" justifyContent="center">
            <LoadingSpinner size={60} />
          </Box>
        )}
        {usersData && modulesData && (
          <>
            <Table data-cy="Assessment-table">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableCellSticky}>Name &amp; Role</TableCell>
                  {modules.map((module) => (
                    <TableCell key={toId(module)} align="center" style={{ whiteSpace: "nowrap", width: 150 }}>
                      {module.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody className={classes.infiniteScrollWrapper}>
                <InfiniteScroll
                  dataLength={items.length || 0}
                  next={loadMore}
                  hasMore
                  scrollableTarget="Assessment-scroller"
                  className={classes.infiniteScroll}
                >
                  {items}
                </InfiniteScroll>
              </TableBody>
            </Table>

            {!loading && items && !items.length && <NoItemsMessage>No matching items</NoItemsMessage>}
          </>
        )}
      </div>
    </>
  )
}

export { Assessment }
